import React from 'react';
import { Link } from 'react-router-dom';
import SalaryTable from '../../components/blog/SalaryTable';
import CTABox from '../../components/blog/CTABox';
import TipBox from '../../components/blog/TipBox';
import FaqAccordion from '../../components/FaqAccordion';
import AuthorBio from '../../components/blog/AuthorBio';
import { SOC_ANALYST_FAQS } from '../../faqs';

const SocAnalystGehalt: React.FC = () => {
  const salaryData = [
    { position: 'SOC Analyst Tier 1', junior: '38.000 - 48.000 €', mid: '45.000 - 55.000 €', senior: '52.000 - 62.000 €', lead: '60.000 - 72.000 €' },
    { position: 'SOC Analyst Tier 2', junior: '45.000 - 55.000 €', mid: '52.000 - 66.000 €', senior: '64.000 - 78.000 €', lead: '75.000 - 90.000 €' },
    { position: 'IT Security Analyst', junior: '45.000 - 55.000 €', mid: '55.000 - 70.000 €', senior: '68.000 - 85.000 €', lead: '82.000 - 98.000 €' },
    { position: 'Incident Responder', junior: '48.000 - 58.000 €', mid: '58.000 - 74.000 €', senior: '72.000 - 92.000 €', lead: '88.000 - 108.000 €' },
    { position: 'Security Engineer', junior: '50.000 - 62.000 €', mid: '62.000 - 80.000 €', senior: '80.000 - 100.000 €', lead: '95.000 - 118.000 €' },
    { position: 'Security Architect', junior: 'n. a.', mid: '75.000 - 92.000 €', senior: '92.000 - 115.000 €', lead: '112.000 - 140.000 €' },
  ];

  return (
    <article className="blog-article">
      <div className="blog-meta">
        <p><strong>Zuletzt aktualisiert:</strong> 8. September 2026</p>
      </div>

      <p>
        Der SOC Analyst ist für die meisten der Einstieg in die IT-Sicherheit. Genau
        deshalb wird die Rolle beim Gehalt regelmäßig falsch eingeordnet, in beide
        Richtungen. Dieser Beitrag trennt die Zahlen nach Tier, Rolle und
        Verantwortung und erklärt, warum die Angaben im Netz so weit auseinandergehen.
      </p>

      <h2>Warum SOC Analyst und IT Security Analyst nicht dasselbe sind</h2>
      <p>
        Beide Bezeichnungen tauchen in Stellenanzeigen oft synonym auf, meinen aber in
        der Praxis unterschiedliche Arbeit. Der SOC Analyst arbeitet im Security
        Operations Center, meist im Schichtbetrieb, und bearbeitet Alarme aus SIEM-
        und EDR-Systemen. Der IT Security Analyst sitzt häufiger in der Linie, betreut
        Richtlinien, Schwachstellenmanagement und Audits.
      </p>
      <p>
        Der Unterschied schlägt aufs Gehalt durch. Die Einstiegsstufe im SOC startet
        niedriger, gleicht das aber teilweise über Schichtzulagen aus. Wer diese
        Zulagen bei einem Wechsel in eine Tagesrolle nicht einrechnet, verhandelt sich
        unbemerkt schlechter.
      </p>

      <h2>Die Zahlen, und warum die Quellen sich widersprechen</h2>
      <p>
        Ein Blick auf aktuelle Auswertungen zeigt eine große Spannweite:
      </p>
      <ul>
        <li>
          <strong>Indeed</strong> nennt für IT-Security-Analysten ein
          Durchschnittsgehalt von 57.000 Euro, mit Einstiegswerten um 48.000 Euro und
          einem oberen Ende bei etwa 80.000 Euro. Die Auswertung beruht auf einer
          kleinen Zahl gemeldeter Gehälter.
        </li>
        <li>
          <strong>StepStone</strong> weist für den Cyber Security Analyst einen Median
          von rund 51.400 Euro aus, bei einer Spanne von 45.300 bis 63.200 Euro.
        </li>
        <li>
          Über <strong>alle IT-Security-Rollen hinweg</strong> liegt das Gros zwischen
          etwa 52.750 Euro und 97.000 Euro, der Durchschnitt bei rund 70.000 Euro.
        </li>
      </ul>
      <p>
        Der scheinbare Widerspruch löst sich, sobald man die Rollen trennt. Die
        niedrigen Werte beschreiben Einstiegspositionen im SOC, die hohen erfahrene
        Security Engineers und Architekten. Wer beides in einen Durchschnitt wirft,
        erhält eine Zahl, die für niemanden stimmt.
      </p>

      <TipBox title="Für Kandidaten">
        <p>
          Fragen Sie im Gespräch nach dem Tier-Modell des SOC und danach, welche
          Aufgaben ab welchem Tier übernommen werden. Ein SOC ohne klare Tier-Struktur
          bedeutet in der Praxis meist, dass Sie Alarme sichten und gleichzeitig
          eskalieren, ohne dass sich das im Gehalt niederschlägt.
        </p>
      </TipBox>

      <SalaryTable
        title="Gehälter in der IT-Sicherheit nach Rolle (Deutschland, 2026)"
        rows={salaryData}
        note="Bruttojahresgehalt für Festanstellungen, ohne Schichtzulagen und Bonus. Berlin liegt bei Security-Rollen im oberen Mittelfeld. Frankfurt und München zahlen im Schnitt mehr, getrieben durch Finanzsektor und Industrie."
      />

      <h2>Was das Gehalt tatsächlich bewegt</h2>

      <h3>Zertifizierungen wirken, aber unterschiedlich stark</h3>
      <p>
        Gängige Auswertungen nennen für CISSP Aufschläge von 15 bis 20 Prozent, für
        CISM 10 bis 18 Prozent und für OSCP im Pentesting-Umfeld 12 bis 20 Prozent.
        Entscheidend ist auch hier, ob dahinter Praxis steht. In Fachgesprächen fällt
        schnell auf, wenn jemand die Prüfung bestanden, aber nie einen echten Incident
        begleitet hat.
      </p>

      <h3>Regulatorik treibt die Nachfrage</h3>
      <p>
        Seit Dezember 2025 verpflichtet die NIS2-Richtlinie rund 20.850 Einrichtungen
        in Deutschland zu systematischer Cybersicherheit, inklusive persönlicher
        Haftung der Geschäftsführung. Tausende Unternehmen suchen dadurch gleichzeitig
        Security-Personal. Der Hays Salary Guide weist für 2025 und 2026 einen Anstieg
        der IT-Security-Gehälter um 8 bis 12 Prozent aus.
      </p>

      <h3>Der Schritt vom Analysten zum Engineer</h3>
      <p>
        Der größte einzelne Gehaltssprung liegt nicht zwischen Tier 1 und Tier 2,
        sondern beim Wechsel vom reaktiven Analysieren zum Bauen und Absichern von
        Systemen. Wer Detection-Regeln schreibt, Automatisierung aufsetzt oder
        Cloud-Umgebungen härtet, verlässt das Analystenband.
      </p>
      <p>
        Besonders gefragt ist die Kombination aus Security und Cloud. Mehr dazu in
        unserem Beitrag zu{' '}
        <Link to="/blog/cloud-architect-gehalt-2026">Cloud-Architektur-Gehältern</Link>.
      </p>

      <h2>Für Unternehmen: warum diese Stellen so lange offen bleiben</h2>
      <p>
        Security-Rollen gehören zu den am schwersten besetzbaren Positionen überhaupt.
        Der Grund ist strukturell: Spezialisierung braucht Zeit, und Zertifizierungen
        wie CISSP oder OSCP lassen sich nicht in sechs Monaten nachholen. Wer heute
        sucht, konkurriert mit allen anderen, die NIS2 gerade umsetzen.
      </p>

      <TipBox title="Für Arbeitgeber">
        <p>
          Wenn eine Security-Stelle seit Monaten offen ist, prüfen Sie zuerst, ob Sie
          Zertifizierung und Erfahrung gleichzeitig verlangen. Diese Kombination ist am
          Markt selten und teuer. Häufig ist es wirtschaftlicher, jemanden mit solider
          Praxis einzustellen und die Zertifizierung zu finanzieren.
        </p>
      </TipBox>

      <h2>Häufige Fragen</h2>
      <FaqAccordion items={SOC_ANALYST_FAQS} />

      <AuthorBio autor="kamran" />

      <p className="sources">
        <strong>Quellen:</strong> Indeed Karriere-Guide, StepStone Gehaltsdaten,
        Hays Salary Guide 2025/2026, Bitkom Studienbericht Arbeitsmarkt für
        IT-Fachkräfte, NIS2-Richtlinie, eigene Vermittlungsdaten
      </p>

      <CTABox
        title="Security-Position zu besetzen?"
        description="Wir sagen Ihnen vorab, ob Anforderungsprofil und Gehaltsband im Berliner Markt zusammenpassen, bevor die Stelle Monate offen steht."
        buttonText="Position besprechen"
        buttonLink="/kontakt"
      />
    </article>
  );
};

export default SocAnalystGehalt;
